import type {
  PortableTextBlock,
  PortableTextMarkDefinition,
  PortableTextSpan,
} from "@portabletext/types";
import type { PlaceholderValues } from "./placeholder-types";

/** Accepts string (legacy), Portable Text blocks, or nullish values. */
export type PortableTextInput = string | PortableTextBlock[] | null | undefined;

interface DynamicPlaceholderMark extends PortableTextMarkDefinition {
  _type: "dynamicPlaceholder";
  placeholderKey?: string;
  fallbackText?: string;
}

const findPlaceholderMark = (
  markKey: string,
  markDefs: DynamicPlaceholderMark[] | undefined,
): DynamicPlaceholderMark | undefined =>
  markDefs?.find(
    (def) => def._key === markKey && def._type === "dynamicPlaceholder",
  );

const resolvePlaceholderValue = (
  value: string | number | PortableTextBlock[],
): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  // Recurse without placeholders to avoid infinite loops
  return resolvePortableTextToString(value, {});
};

const resolveSpan = (
  span: PortableTextSpan,
  markDefs: DynamicPlaceholderMark[] | undefined,
  placeholders: PlaceholderValues,
): string => {
  const placeholderMarkKey = span.marks?.find((key) =>
    findPlaceholderMark(key, markDefs),
  );

  if (!placeholderMarkKey) {
    return span.text || "";
  }

  const markDef = findPlaceholderMark(placeholderMarkKey, markDefs);
  const { placeholderKey, fallbackText = span.text || "" } = markDef || {};

  if (placeholderKey && placeholders[placeholderKey] != null) {
    return resolvePlaceholderValue(placeholders[placeholderKey]);
  }

  return fallbackText;
};

const resolveBlock = (
  block: PortableTextBlock,
  placeholders: PlaceholderValues,
): string => {
  if (block._type !== "block" || !Array.isArray(block.children)) {
    return "";
  }

  const markDefs = block.markDefs as DynamicPlaceholderMark[] | undefined;

  return block.children
    .filter((child): child is PortableTextSpan => child._type === "span")
    .map((span) => resolveSpan(span, markDefs, placeholders))
    .join("");
};

/**
 * Converts Portable Text with dynamic placeholders to a plain string.
 * Supports legacy string values for backward compatibility.
 */
export function resolvePortableTextToString(
  value: PortableTextInput,
  placeholders: PlaceholderValues = {},
): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (!Array.isArray(value)) {
    return "";
  }

  return value
    .map((block) => resolveBlock(block, placeholders))
    .join("")
    .trim();
}
